/**
 * RecipeCard — a recipe the customer can actually shop.
 *
 * Quantities are rescaled by the SERVINGS STEPPER in code (the model is never
 * asked to do arithmetic). An ingredient the store's catalog knows becomes a
 * tappable catalog line; anything else stays a plain note and can never be
 * added, which is how an invented product is impossible.
 *
 * Nothing is ever added without a tap: "Add everything to my list" or
 * "Let me choose" -> checklist -> "Add selected".
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Clock, Copy, Loader2, Minus, Plus, Share2, Star, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import GroceryIcon from '../grocery/GroceryIcon';

/** PLAIN TEXT A PERSON CAN READ IN ANY APP — no markup, no emoji, no mention of
 *  how the recipe was written. Amounts are the SCALED ones on screen, because
 *  that is the recipe the customer is actually looking at. */
export const recipeAsText = (recipe, lines, t, storeName) => {
    const out = [recipe.title];
    if (recipe.servings) out.push(t('askChef.shareServings', { n: recipe.servings }));
    out.push('', t('askChef.ingredients').toUpperCase());
    (lines || []).forEach((i) => {
        const amount = (i.amount || '').trim();
        const name = i.catalog_name || i.label || '';
        out.push(amount ? `- ${name} (${amount})` : `- ${name}`);
    });
    const steps = recipe.steps || [];
    if (steps.length) {
        out.push('', t('askChef.steps').toUpperCase());
        steps.forEach((s, n) => out.push(`${n + 1}. ${typeof s === 'string' ? s : s.text || ''}`));
    }
    out.push('', storeName ? t('askChef.shareClosing', { store: storeName })
        : t('askChef.shareClosingPlain'));
    return out.join('\n');
};

const copyText = async (text) => {
    try {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch (_e) { /* fall through to the old way */ }
    try {                                   // older browsers and insecure origins
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        return ok;
    } catch (_e) { return false; }
};

export default function RecipeCard({ recipe, canAdd = true, kid = false, extras = [],
    onScale, onAdd, onAddAsWritten, onSave, saving = false, storeName = '',
    testId = 'chef-recipe-card' }) {
    const { t } = useTranslation();
    const [choosing, setChoosing] = useState(false);
    const [picked, setPicked] = useState({});
    const [busy, setBusy] = useState(false);
    const [copied, setCopied] = useState(false);
    const [written, setWritten] = useState({});

    const shoppable = useMemo(
        () => (recipe.ingredients || []).filter((i) => i.catalog_item_id), [recipe]);
    const notes = useMemo(
        () => (recipe.ingredients || []).filter((i) => !i.catalog_item_id), [recipe]);

    useEffect(() => {
        const next = {};
        shoppable.forEach((i) => { next[i.catalog_item_id] = true; });
        extras.forEach((e) => { next[e.catalog_item_id] = true; });
        setPicked(next);
    }, [shoppable, extras]);

    const lines = [...shoppable, ...extras];
    const allLines = [...lines, ...notes];
    const shareText = () => recipeAsText(recipe, allLines, t, storeName);
    const doCopy = async () => {
        const ok = await copyText(shareText());
        setCopied(ok);
        setTimeout(() => setCopied(false), 2500);
    };
    const doSend = async () => {
        // The phone's OWN sheet: SMS, WhatsApp, Messenger, mail, whatever the
        // customer has. No share sheet on this device means Copy instead.
        if (navigator.share) {
            try {
                await navigator.share({ title: recipe.title, text: shareText() });
                return;
            } catch (_e) { return; }        // the customer cancelled: say nothing
        }
        await doCopy();
    };
    const add = async (onlyPicked) => {
        setBusy(true);
        try {
            const rows = (onlyPicked ? lines.filter((i) => picked[i.catalog_item_id]) : shoppable)
                .map((i) => ({ catalog_item_id: i.catalog_item_id, label: i.catalog_name || i.label,
                    quantity: i.quantity || 1, options: i.options || [] }));
            const r = await onAdd(rows);
            if (r === null) return;              // it failed and already said so
            setChoosing(false);
        } finally { setBusy(false); }
    };

    /** THE WHOLE RECIPE, ONE TAP, NO DEAD ENDS.
     *
     *  Everything the store's catalog knows goes in as a catalog line, which is
     *  what carries the household's saved kind and brand and the member the
     *  "who's adding" chip is on. Anything the catalog does not have still goes
     *  in, in the recipe's own words, so it is saved for this household from
     *  now on instead of being left behind. One confirmation for the lot. */
    const addEverythingToCart = async () => {
        setBusy(true);
        try {
            const rows = shoppable.map((i) => ({
                catalog_item_id: i.catalog_item_id, label: i.catalog_name || i.label,
                quantity: i.quantity || 1, options: i.options || [] }));
            const jobs = [];
            if (rows.length) jobs.push(onAdd(rows, { quiet: true }));
            if (onAddAsWritten) notes.forEach((n) => jobs.push(onAddAsWritten(n.label, { quiet: true })));
            const done = await Promise.all(jobs);
            if (!jobs.length || done.every((d) => d === null)) return;  // it failed and said so
            setWritten((w) => {
                const next = { ...w };
                notes.forEach((n) => { next[n.label] = true; });
                return next;
            });
            setChoosing(false);
            toast.success(t('askChef.addedToCart'));
        } finally { setBusy(false); }
    };

    return (
        <div className="space-y-4 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900"
            data-testid={testId}>
            <div>
                <h3 className="text-lg font-black leading-tight text-foreground" data-testid={`${testId}-title`}>
                    {recipe.title}
                </h3>
                <div className="mt-1 flex flex-wrap items-center gap-3 text-xs font-semibold text-muted-foreground">
                    {recipe.time_minutes ? (
                        <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />
                            {t('askChef.minutes', { n: recipe.time_minutes })}</span>
                    ) : null}
                    <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />
                        {t('askChef.servings')}</span>
                </div>
            </div>

            {/* Servings stepper — rescaling happens in code */}
            <div className="flex items-center justify-between gap-3 rounded-2xl bg-slate-50 px-3 py-2 dark:bg-slate-800/60"
                data-testid={`${testId}-servings`}>
                <span className="text-sm font-semibold text-slate-600 dark:text-slate-300">
                    {t('askChef.servings')}
                </span>
                <div className="flex items-center gap-3">
                    <button onClick={() => onScale(Math.max(1, (recipe.servings || 4) - 1))}
                        disabled={(recipe.servings || 4) <= 1} data-testid={`${testId}-servings-minus`}
                        aria-label={t('grocery.fewer')}
                        className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-300 disabled:opacity-40 dark:border-slate-600">
                        <Minus className="h-4 w-4" />
                    </button>
                    <span key={recipe.servings} data-testid={`${testId}-servings-value`}
                        className="play-bump min-w-[2rem] text-center text-xl font-black tabular-nums text-foreground">
                        {recipe.servings || 4}
                    </span>
                    <button onClick={() => onScale(Math.min(24, (recipe.servings || 4) + 1))}
                        disabled={(recipe.servings || 4) >= 24} data-testid={`${testId}-servings-plus`}
                        aria-label={t('grocery.more')}
                        className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-300 disabled:opacity-40 dark:border-slate-600">
                        <Plus className="h-4 w-4" />
                    </button>
                </div>
            </div>

            <div className="space-y-2">
                <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                    {t('askChef.ingredients')}
                </p>
                <ul className="space-y-1.5" data-testid={`${testId}-ingredients`}>
                    {lines.map((i, n) => (
                        // Two rows CAN carry the same catalog item (brown sugar
                        // and white sugar are both "sugar"), so the key has to
                        // carry the position as well as the item.
                        <li key={`${i.catalog_item_id}-${n}`}
                            data-testid={`${testId}-ing-${i.catalog_item_id}`}
                            className="flex items-center gap-2.5 rounded-xl border border-slate-200 px-2.5 py-2 dark:border-slate-700">
                            {choosing ? (
                                <button role="checkbox" aria-checked={!!picked[i.catalog_item_id]}
                                    aria-label={i.catalog_name || i.label}
                                    onClick={() => setPicked((p) => ({ ...p,
                                        [i.catalog_item_id]: !p[i.catalog_item_id] }))}
                                    data-testid={`${testId}-pick-${i.catalog_item_id}`}
                                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md border ${
                                        picked[i.catalog_item_id]
                                            ? 'border-emerald-500 bg-emerald-500'
                                            : 'border-slate-300 dark:border-slate-600'}`}>
                                    {picked[i.catalog_item_id]
                                        ? <Check className="h-4 w-4 text-white" /> : null}
                                </button>
                            ) : null}
                            {i.icon ? <GroceryIcon icon={i.icon} badge={i.badge} size={26} alt="" /> : null}
                            <span className="min-w-0 flex-1">
                                <span className="block truncate text-sm font-semibold text-foreground">
                                    {i.catalog_name || i.label}
                                </span>
                                <span className="block text-[11px] text-muted-foreground">
                                    {[i.amount, i.line].filter(Boolean).join(' · ')}
                                </span>
                            </span>
                            {i.sponsored !== undefined ? (
                                <span data-testid={`${testId}-label-${i.catalog_item_id}`}
                                    className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                                        i.sponsored ? 'bg-[#F58220] text-[#13265C]' : 'bg-slate-100 text-slate-600'}`}>
                                    {i.sponsored ? t('askChef.sponsored') : t('askChef.sample')}
                                </span>
                            ) : null}
                        </li>
                    ))}
                </ul>
                {notes.length ? (
                    <ul className="space-y-1 pt-1" data-testid={`${testId}-notes`}>
                        {notes.map((n, idx) => (
                            <li key={`${n.label}-${idx}`} className="flex items-center gap-2 text-xs text-muted-foreground">
                                <span className="min-w-0 flex-1">
                                    · {n.label} {n.amount ? `(${n.amount})` : ''} — {t('askChef.notInCatalog')}
                                </span>
                                {/* NOT A DEAD END: the CUSTOMER adds it in those
                                    words. The chef never adds anything itself. */}
                                {canAdd && onAddAsWritten ? (
                                    <button onClick={async () => {
                                        setBusy(true);
                                        try { await onAddAsWritten(n.label); setWritten(
                                            (w) => ({ ...w, [n.label]: true })); }
                                        finally { setBusy(false); }
                                    }} disabled={busy || !!written[n.label]}
                                        data-testid={`${testId}-as-written-${idx}`}
                                        className="min-h-[36px] shrink-0 rounded-full border border-emerald-400 px-3 text-[11px] font-bold text-emerald-700 disabled:opacity-50 dark:text-emerald-300">
                                        {written[n.label] ? t('grocery.savedShort') : t('grocery.addAsWritten')}
                                    </button>
                                ) : null}
                            </li>
                        ))}
                    </ul>
                ) : null}
            </div>

            <div className="space-y-2">
                <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                    {t('askChef.steps')}
                </p>
                <ol className="space-y-2" data-testid={`${testId}-steps`}>
                    {(recipe.steps || []).map((s, idx) => (
                        <li key={idx} className="flex gap-2.5 text-sm text-foreground">
                            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-900 text-[11px] font-bold text-white dark:bg-white dark:text-slate-900">
                                {idx + 1}
                            </span>
                            <span>{s}</span>
                        </li>
                    ))}
                </ol>
            </div>

            {/* COPY AND SEND — the recipe leaves the app as plain text, through
                the phone's own share sheet. Available in kid mode too, where
                the only thing shared is the recipe text. */}
            <div className="flex gap-2">
                <Button variant="outline" onClick={doCopy}
                    data-testid={`${testId}-copy`}
                    className="min-h-[48px] flex-1 gap-2 text-base font-semibold">
                    {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
                    {copied ? t('askChef.copied') : t('askChef.copy')}
                </Button>
                <Button variant="outline" onClick={doSend}
                    data-testid={`${testId}-send`}
                    className="min-h-[48px] flex-1 gap-2 text-base font-semibold">
                    <Share2 className="h-5 w-5" />
                    {t('askChef.send')}
                </Button>
            </div>

            {onSave ? (
                <button onClick={onSave} disabled={saving} data-testid={`${testId}-save`}
                    className="flex min-h-[44px] w-full items-center justify-center gap-2 text-sm font-bold text-emerald-600">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Star className="h-4 w-4" />}
                    {t('askChef.saveRecipe')}
                </button>
            ) : null}
            {kid ? null : (
                <p className="text-[11px] text-muted-foreground">{t('askChef.privacy')}</p>
            )}
            {!canAdd ? (
                <p className="rounded-2xl bg-[#13265C]/5 px-3 py-2 text-sm font-semibold text-[#13265C] dark:bg-white/10 dark:text-white"
                    data-testid={`${testId}-cannot-add`}>
                    {t('askChef.askGrownUp')}
                </p>
            ) : (
                // A FRAGMENT, NOT A WRAPPER: `position: sticky` is held inside its
                // own parent box, so a wrapper the height of the button itself
                // gives it nothing to stick against. As a direct child of the
                // card, it has the whole recipe above it to stay pinned over.
                <>
                    {choosing ? null : (
                        <Button variant="outline" onClick={() => setChoosing(true)}
                            data-testid={`${testId}-let-me-choose`}
                            className="mb-2 min-h-[52px] w-full text-base font-semibold">
                            {t('askChef.letMeChoose')}
                        </Button>
                    )}
                    {/* PINNED TO THE BOTTOM so it is on screen at any recipe
                        length, over its own backing so nothing reads through
                        it, and padded past the phone's home indicator. */}
                    <div className="yot-sticky-action sticky bottom-0 -mx-4 border-t border-slate-200 bg-white px-4 pb-[calc(0.5rem+var(--yot-safe-bottom))] pt-2 shadow-[0_-8px_16px_-12px_rgba(15,23,42,0.35)] dark:border-slate-700 dark:bg-slate-900"
                        data-testid={`${testId}-action-bar`}>
                        {choosing ? (
                            <Button onClick={() => add(true)} disabled={busy}
                                data-testid={`${testId}-add-selected`}
                                className="min-h-[56px] w-full gap-2 text-base font-bold">
                                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Check className="h-5 w-5" />}
                                {t('askChef.addSelected')}
                            </Button>
                        ) : (
                            <Button onClick={addEverythingToCart} disabled={busy}
                                data-testid={`${testId}-add-all`}
                                className="min-h-[56px] w-full gap-2 text-base font-bold">
                                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Plus className="h-5 w-5" />}
                                {t('askChef.addToCart')}
                            </Button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
